package com.evoapps.evoshift.domain.usecase.availability

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.availability.AvailabilitySlotMapping
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.AvailabilitySharing
import com.evoapps.evoshift.domain.model.SharedAvailability
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository

/**
 * SH-135: where a person's one availability goes. Each of their active workplaces whose owner
 * they sent it to ([SharedAvailability.releasedToOwnerIds]) gets a copy on its own slots; other
 * owners' workplaces only when they share it with all employers. A copy the workplace's managers
 * locked is left as it is.
 */
class AvailabilityDistribution(
    private val availabilityRepository: AvailabilityRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository
) {
    /** The person's active workplaces that can be read (a deleted one is skipped). */
    suspend fun workplacesOf(userId: String): List<Workplace> {
        val user = userRepository.getUserProfile(userId).getOrThrow()
        return user.engagements.values.filter { it.isActive }.mapNotNull { workplaceRepository.getWorkplace(it.workplaceId).getOrNull() }
            .sortedBy { it.name }
    }

    /** The workplaces that get [released]'s availability. */
    fun targets(workplaces: List<Workplace>, released: Set<String>, sharing: AvailabilitySharing): List<Workplace> =
        workplaces.filter { sharing.sharesWithAll || it.ownerId in released }

    /**
     * The month as one document from the per-workplace documents written before SH-135 (no loss:
     * comments stay with their workplace), or null when there are none.
     */
    suspend fun fromLegacy(userId: String, month: String, workplaces: List<Workplace>): SharedAvailability? {
        val legacy = workplaces.mapNotNull { wp ->
            availabilityRepository.findAvailability(userId, wp.id, month).getOrNull()?.let { it to wp.settings.definedAvailabilitySlots }
        }
        if (legacy.isEmpty()) return null
        val owners = workplaces.associate { it.id to it.ownerId }
        return AvailabilitySlotMapping.merge(userId, month, legacy) { owners[it] }
    }

    /**
     * Writes [shared] to every target workplace and removes it from workplaces that are no longer
     * targets (the person stopped sharing). Failures are per workplace.
     */
    suspend fun distribute(shared: SharedAvailability, workplaces: List<Workplace>, sharing: AvailabilitySharing): DistributionOutcome {
        val targets = targets(workplaces, shared.releasedToOwnerIds, sharing).map { it.id }.toSet()
        val sent = mutableListOf<String>()
        val locked = mutableListOf<String>()
        val failed = mutableListOf<String>()
        val removed = mutableListOf<String>()
        workplaces.forEach { wp ->
            val current: Availability? = availabilityRepository.findAvailability(shared.userId, wp.id, shared.month).getOrNull()
            when {
                current?.isLocked == true -> if (wp.id in targets) locked += wp.id
                wp.id in targets -> availabilityRepository.saveAvailability(
                    AvailabilitySlotMapping.toWorkplace(shared, wp.id, wp.settings.definedAvailabilitySlots)
                ).fold({ sent += wp.id }, { failed += wp.id })
                current != null -> availabilityRepository.deleteAvailability(shared.userId, wp.id, shared.month)
                    .fold({ removed += wp.id }, { failed += wp.id })
            }
        }
        return DistributionOutcome(sent, locked, failed, removed)
    }
}

/** Workplace ids: given the month, locked by their managers (kept as they were), failed, no longer given it. */
data class DistributionOutcome(
    val sentTo: List<String>,
    val locked: List<String>,
    val failed: List<String>,
    val removedFrom: List<String> = emptyList()
)

/** SH-135: the person's month as the form at one workplace shows it. */
data class MyAvailability(
    /** The month for all workplaces (from the shared document, the older per-workplace ones, or empty). */
    val shared: SharedAvailability,
    /** The workplace the form is filled at; null when it cannot be read. */
    val workplace: Workplace?,
    /** The workplace has the month, sent (filled in). */
    val sentHere: Boolean,
    /** The workplace's managers locked its copy. */
    val lockedHere: Boolean,
    /** Every workplace the month goes to now, the form's own included once sent. */
    val goesTo: List<Workplace>,
    /** The person works for more than one employer (the sharing choice matters). */
    val employers: Int,
    val sharing: AvailabilitySharing
)

/**
 * SH-135: the person's month for the form at [workplaceId]: one availability for every workplace;
 * before the first answer after SH-135, the older per-workplace documents read as one.
 */
class GetMyAvailabilityUseCase(
    private val availabilityRepository: AvailabilityRepository,
    private val distribution: AvailabilityDistribution
) {
    suspend operator fun invoke(userId: String, workplaceId: String, month: String): Result<MyAvailability> = runCatching {
        val workplaces = distribution.workplacesOf(userId)
        val here = workplaces.firstOrNull { it.id == workplaceId }
        val sharing = availabilityRepository.getSharing(userId).getOrDefault(AvailabilitySharing())
        val shared = availabilityRepository.getSharedAvailability(userId, month).getOrNull()
            ?: distribution.fromLegacy(userId, month, workplaces)
            ?: SharedAvailability(userId = userId, month = month, days = emptyMap())
        val copy = availabilityRepository.findAvailability(userId, workplaceId, month).getOrNull()
        MyAvailability(
            shared = shared,
            workplace = here,
            sentHere = copy?.isFilled == true,
            lockedHere = copy?.isLocked == true,
            goesTo = distribution.targets(workplaces, shared.releasedToOwnerIds + listOfNotNull(here?.ownerId), sharing),
            employers = workplaces.map { it.ownerId }.distinct().size,
            sharing = sharing
        )
    }
}

/** SH-135: the sharing choice and whether to ask it (the person works for two or more employers and was never asked). */
data class SharingState(val sharing: AvailabilitySharing, val employers: Int) {
    val shouldAsk: Boolean get() = employers >= 2 && !sharing.wasAsked
}

class GetAvailabilitySharingUseCase(
    private val availabilityRepository: AvailabilityRepository,
    private val distribution: AvailabilityDistribution
) {
    suspend operator fun invoke(userId: String): Result<SharingState> = runCatching {
        val employers = distribution.workplacesOf(userId).map { it.ownerId }.distinct().size
        SharingState(availabilityRepository.getSharing(userId).getOrThrow(), employers)
    }
}

/**
 * SH-135: "Share my availability with all employers": saves the choice and applies it to the
 * months from [fromMonth] ("YYYY-MM") already given: on, every workplace gets them; off, other
 * employers' copies are removed (unless their managers locked them), the ones the person sent
 * there themselves stay.
 */
class SetAvailabilitySharingUseCase(
    private val availabilityRepository: AvailabilityRepository,
    private val distribution: AvailabilityDistribution
) {
    suspend operator fun invoke(userId: String, shareWithAllEmployers: Boolean, fromMonth: String): Result<Unit> = runCatching {
        val sharing = AvailabilitySharing(shareWithAllEmployers, AppClock.now().toString())
        availabilityRepository.saveSharing(userId, sharing).getOrThrow()
        val months = availabilityRepository.getSharedAvailabilityFrom(userId, fromMonth).getOrDefault(emptyList())
        if (months.isEmpty()) return@runCatching
        val workplaces = distribution.workplacesOf(userId)
        months.forEach { distribution.distribute(it, workplaces, sharing) }
    }
}
